import { memo, useCallback, useState, useEffect } from "react";
import { useDebouncedCallback } from "use-debounce";
import { AllocationHeader } from "./allocation/AllocationHeader";
import { AllocationInputs } from "./allocation/AllocationInputs";
import { Checkbox } from "@/components/ui/checkbox";
import { getFundDisplayName } from "@/utils/fundDisplayUtils";
interface AllocationItemProps {
  allocation: {
    id: string;
    asset_id: string;
    asset_type?: string;
    allocation_value?: number | null;
    is_value_locked?: boolean;
    fund?: {
      id?: string;
      fund_name?: string;
      fund_strategy?: string;
      investment_strategy?: string;
      asset_class?: string;
      // Add fields needed for stock display
      display_name?: string;
      name?: string;
      ticker?: string;
      company_name?: string;
      sa_asset_class?: string;
      sa_sub_asset_class?: string;
    };
    allocation_percentage: number;
  };
  portfolioSize: number;
  isPortfolioSizeLocked: boolean;
  onDelete: (id: string) => void;
  onUpdatePercentage: (id: string, value: number) => void;
  onUpdateDollarAmount: (allocation: any, value: number, isPortfolioSizeLocked: boolean) => void;
  onSuccess: () => void;
  selected?: boolean;
  onToggleSelect?: (id: string) => void;
  // Editing mode based on portfolio type
  mode?: 'percentage' | 'dollar';
  // Optional callbacks to support optimistic subtotal updates upstream
  onLocalPercentageChange?: (allocationId: string, newPct: number) => void;
  onLocalDollarChange?: (allocationId: string, newDollars: number) => void;
  // Display currency code for input symbol rendering
  currencyCode?: string;
}

export const AllocationItem = memo(({ 
  allocation,
  portfolioSize,
  isPortfolioSizeLocked,
  onDelete,
  onUpdatePercentage,
  onUpdateDollarAmount,
  onSuccess,
  selected,
  onToggleSelect,
  mode,
  onLocalPercentageChange,
  onLocalDollarChange,
  currencyCode,
}: AllocationItemProps) => {
  const effectiveMode: 'percentage' | 'dollar' = mode ?? 'percentage';
  const percentageInputId = `percentage-${allocation.id}`;
  const dollarInputId = `dollar-${allocation.id}`;

  const [percentageInput, setPercentageInput] = useState((Number(allocation.allocation_percentage) || 0).toFixed(2));
  const [dollarInput, setDollarInput] = useState(() => {
    // In dollar mode, prefer allocation_value if present; otherwise derive from percentage
    const initialDollar = (effectiveMode === 'dollar' && typeof allocation.allocation_value === 'number')
      ? allocation.allocation_value
      : ((allocation.allocation_percentage * portfolioSize) / 100);
    return (initialDollar || 0).toFixed(2);
  });

  // Allocation management is handled upstream; this item focuses on UI and input handling.

  // Sync inputs when allocation data changes (after DB updates)
  useEffect(() => {
    // Only update inputs from DB when the respective field is NOT focused, to prevent
    // fighting with the user's in-progress edits. When we do update, format to 2 decimals.
    const activeId = (typeof document !== 'undefined' && document.activeElement)
      ? (document.activeElement as HTMLElement).id
      : '';

    if (activeId !== percentageInputId) {
      // In dollar mode, prefer deriving percentage from dollar value and current portfolio size
      if (effectiveMode === 'dollar' && typeof allocation.allocation_value === 'number' && (portfolioSize || 0) > 0) {
        const derivedPct = (allocation.allocation_value / (portfolioSize || 1)) * 100;
        setPercentageInput((Number.isFinite(derivedPct) ? derivedPct : 0).toFixed(2));
      } else {
        setPercentageInput((Number(allocation.allocation_percentage) || 0).toFixed(2));
      }
    }
    
    const newDollarValue = (effectiveMode === 'dollar' && typeof allocation.allocation_value === 'number')
      ? allocation.allocation_value
      : ((allocation.allocation_percentage * portfolioSize) / 100);
    if (activeId !== dollarInputId) {
      setDollarInput((newDollarValue || 0).toFixed(2));
    }
  }, [allocation.allocation_percentage, allocation.allocation_value, effectiveMode, portfolioSize, percentageInputId, dollarInputId]);

  const debouncedUpdatePercentage = useDebouncedCallback((value: number) => {
    // Input validation: bounds checking for percentages (0-1000% max)
    if (!isNaN(value) && value >= 0 && value <= 1000) {
      onUpdatePercentage(allocation.id, Number(value.toFixed(2)));
    }
  }, 100);

  const debouncedUpdateDollarAmount = useDebouncedCallback((value: number) => {
    if (!isNaN(value) && value >= 0) {
      onUpdateDollarAmount(allocation, Number(value.toFixed(2)), isPortfolioSizeLocked);
    }
  }, 100);

  const handlePercentageChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setPercentageInput(inputValue);

    // Handle empty/cleared input as zero
    if (inputValue === '' || inputValue === '0') {
      setDollarInput('0');
      debouncedUpdatePercentage(0);
      return;
    }

    // Sanitize to allow inputs like "100%" or with stray spaces
    const sanitized = (inputValue || '').replace(/[^0-9.\-]/g, '');
    const numericValue = parseFloat(sanitized);
    if (!isNaN(numericValue)) {
      // Real-time sync: use current portfolioSize to avoid race conditions
      const currentPortfolioSize = portfolioSize || 0;
      if (currentPortfolioSize > 0) {
        const newDollarValue = (numericValue * currentPortfolioSize) / 100;
        setDollarInput(newDollarValue.toFixed(2));
      }
      // Fire optimistic callback so parent can update subtotals immediately
      onLocalPercentageChange?.(allocation.id, numericValue);
      
      debouncedUpdatePercentage(numericValue);
    }
  }, [debouncedUpdatePercentage, portfolioSize]);

  const handleDollarChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setDollarInput(inputValue);

    // Handle empty/cleared input as zero
    if (inputValue === '' || inputValue === '0') {
      setPercentageInput('0');
      debouncedUpdateDollarAmount(0);
      return;
    }

    // Sanitize to allow inputs like "5,000,000" or "$5000000"
    const sanitized = (inputValue || '').replace(/[^0-9.\-]/g, '');
    const numericValue = parseFloat(sanitized);
    if (!isNaN(numericValue)) {
      // Real-time sync: use current portfolioSize to avoid race conditions
      const currentPortfolioSize = portfolioSize || 0;
      if (currentPortfolioSize > 0) {
        const newPercentage = (numericValue / currentPortfolioSize) * 100;
        setPercentageInput(newPercentage.toFixed(2));
        // Fire optimistic callback for dollar-driven edits
        onLocalDollarChange?.(allocation.id, numericValue);
      }
      
      debouncedUpdateDollarAmount(numericValue);
    }
  }, [debouncedUpdateDollarAmount, portfolioSize]);

  const handleBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value);
    if (!isNaN(value)) {
      if (e.target.id === percentageInputId) {
        setPercentageInput(value.toFixed(2));
        // Ensure the last percentage edit persists: schedule and flush immediately
        debouncedUpdatePercentage(value);
        (debouncedUpdatePercentage as any)?.flush?.();
      } else if (e.target.id === dollarInputId) {
        setDollarInput(value.toFixed(2));
        // Ensure the last dollar edit persists: schedule and flush immediately
        debouncedUpdateDollarAmount(value);
        (debouncedUpdateDollarAmount as any)?.flush?.();
      }
    }
  }, [percentageInputId, dollarInputId, debouncedUpdatePercentage, debouncedUpdateDollarAmount]);

  return (
    <div className="flex flex-col p-3 bg-muted rounded-lg">
      <div className="flex items-start gap-3">
        {onToggleSelect && (
          <Checkbox
            checked={!!selected}
            onCheckedChange={() => onToggleSelect(allocation.id)}
            aria-label="Select allocation"
            className="mt-1"
          />
        )}
        <div className="flex-grow space-y-2 min-w-0">
          <AllocationHeader
            fundId={allocation.fund?.id || allocation.asset_id}
            fundName={getFundDisplayName(allocation.fund)}
            onDelete={onDelete}
            allocationId={allocation.id}
            isAsset={!!allocation.asset_id} // Pass true if this is an asset allocation
            assetType={allocation.asset_type}
            fundKind={(allocation.fund as any)?.kind}
          />
          <AllocationInputs
            percentageInputId={percentageInputId}
            dollarInputId={dollarInputId}
            percentageInput={percentageInput}
            dollarInput={dollarInput}
            onPercentageChange={handlePercentageChange}
            onDollarChange={handleDollarChange}
            onBlur={handleBlur}
            mode={effectiveMode}
            currencyCode={currencyCode}
          />
        </div>
      </div>
    </div>
  );
});

AllocationItem.displayName = 'AllocationItem';
